import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles, withStyles } from '@material-ui/core/styles';

import Translate from '@hydra/common/components/i18n';
import CriteriumTable from '@hydra/common/criterium/CriteriumTable';
import Icon from '@hydra/common/components/Icon';

import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import StructuredDataIssueText from '@hydra/common/components/StructuredDataIssueText';
import CriteriumIconAndText from '@hydra/common/criterium/CriteriumIconAndText';

const Accordion = withStyles({
  root: {
    boxShadow: 'none',
    '&:before': {
      display: 'none'
    },
    '&$expanded': {
      margin: 'auto'
    }
  },
  expanded: {}
})(MuiAccordion);

const AccordionSummary = withStyles(theme => ({
  root: {
    padding: 0,
    minHeight: 16,
    '&$expanded': {
      minHeight: 16
    }
  },
  content: {
    margin: theme.spacing(0.25, 0),
    '&$expanded': {
      margin: theme.spacing(0.25, 0)
    }
  },
  expanded: {}
}))(MuiAccordionSummary);

const AccordionDetails = withStyles({
  root: {
    padding: 0
  }
})(MuiAccordionDetails);

const useStyles = makeStyles(theme => ({
  block: {
    border: 'none',
    fontSize: 'unset',
    width: '100%'
  },
  icon: {
    padding: 0
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  errors: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(1)
  },
  warnings: {
    color: theme.palette.warning.main,
    marginLeft: theme.spacing(1)
  }
}));

export const CriteriumSchemaOrgTable = (props) => {
  const classes = useStyles();
  const { items, issues, isPdf } = props;

  const { errors, warnings } = issues.reduce((result, issue) => {
    !issue.level || issue.level === 'error' ? result.errors.push(issue) : result.warnings.push(issue);
    return result;
  }, { errors: [], warnings: [] });

  const errorsText = `structured_data_count_error${errors.length > 1 ? 's' : ''}`;
  const warningsText = `structured_data_count_warning${warnings.length > 1 ? 's' : ''}`;
  const head = [
    <div className={classes.head} key='structured_data_issues'>
      <Translate content='structured_data_issues' />
      <div>
        {errors.length > 0 && <Translate className={classes.errors} content={errorsText} with={{ count: errors.length }} />}
        {warnings.length > 0 && <Translate className={classes.warnings} content={warningsText} with={{ count: warnings.length }} />}
      </div>
    </div>
  ];

  const rows = [...errors, ...warnings].map((issue, i) => {
    const hasExpandingBlock = issue.meta.loc;
    const text = <StructuredDataIssueText component='span' key={i} issue={issue} />;
    const issueText = !issue.level || issue.level === 'error'
      ? <CriteriumIconAndText success={false} text={text} />
      : <CriteriumIconAndText icon={{ type: 'woorank-warning', color: 'warning' }} text={text} />;

    const row = isPdf
      ? issueText
      : (
        <Accordion
          classes={{
            root: classes.expandPanel
          }}
          onChange={e => e.stopPropagation()}
        >
          <AccordionSummary
            expandIcon={hasExpandingBlock && <Icon type='chevron-down' fill='grey' />}
            IconButtonProps={{
              classes: {
                root: classes.icon
              }
            }}
          >
            {issueText}
          </AccordionSummary>
          {
            hasExpandingBlock &&
              (
                <AccordionDetails>
                  <pre className={classes.block}>
                    {JSON.stringify(items[Number(issue.meta.loc[0])], undefined, 2)}
                  </pre>
                </AccordionDetails>
              )
          }
        </Accordion>
        );

    return [row];
  });

  return <CriteriumTable head={head} rows={rows} striped={isPdf} />;
};

CriteriumSchemaOrgTable.propTypes = {
  items: PropTypes.array,
  issues: PropTypes.array,
  isPdf: PropTypes.bool
};

export default CriteriumSchemaOrgTable;
